import React, { useState } from 'react';
import { ChevronDown, Zap, DollarSign } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ModelConfig {
  id: 'MISTRAL' | '20B' | '120B';
  name: string;
  description: string;
  costPerHour: number;
  gpu: string;
  bestFor: string[];
}

const ALL_MODELS: Record<string, ModelConfig> = {
  'MISTRAL': {
    id: 'MISTRAL',
    name: 'Qwen3 14B (Local)',
    description: 'Always-On (Free)',
    costPerHour: 0,
    gpu: 'Local GPU',
    bestFor: ['Background tasks', 'Anomaly detection', 'Quick queries', 'Local dev']
  },
  '20B': {
    id: '20B',
    name: '20B Model',
    description: 'Cost Effective',
    costPerHour: 0.49,
    gpu: 'A40',
    bestFor: ['Summaries', 'Simple queries', 'Routine tasks']
  },
  '120B': {
    id: '120B',
    name: '120B Model',
    description: 'High Performance',
    costPerHour: 2.69,
    gpu: 'H100',
    bestFor: ['Complex analysis', 'Detailed insights', 'Strategic planning']
  }
};

// Filter models based on environment (hide MISTRAL on Railway by default)
const isRailway = import.meta.env.VITE_RAILWAY_ENVIRONMENT === 'true';

interface ModelSelectorProps {
  currentModel: 'MISTRAL' | '20B' | '120B';
  onModelChange: (model: 'MISTRAL' | '20B' | '120B') => Promise<void>;
  availableModels?: Array<'MISTRAL' | '20B' | '120B'>;
  disabled?: boolean;
  aiStatus: 'running' | 'stopped';
}

export const ModelSelector: React.FC<ModelSelectorProps> = ({
  currentModel,
  onModelChange,
  availableModels,
  disabled = false,
  aiStatus
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const { toast } = useToast();

  const fallbackModelIds: Array<'MISTRAL' | '20B' | '120B'> = isRailway
    ? ['120B', '20B']
    : ['MISTRAL', '120B', '20B'];
  const selectedModelIds = (availableModels && availableModels.length > 0 ? availableModels : fallbackModelIds)
    .filter((id) => Boolean(ALL_MODELS[id]));
  const MODELS = selectedModelIds.reduce<Record<string, ModelConfig>>((acc, id) => {
    acc[id] = ALL_MODELS[id];
    return acc;
  }, {});

  const handleModelSelect = async (modelId: 'MISTRAL' | '20B' | '120B') => {
    if (modelId === currentModel) {
      setIsOpen(false);
      return;
    }

    setSwitching(true);
    try {
      // Show cost impact warning (use ALL_MODELS for fallback)
      const current = MODELS[currentModel] || ALL_MODELS[currentModel];
      const target = MODELS[modelId] || ALL_MODELS[modelId];
      const costDiff = target.costPerHour - current.costPerHour;
      const costImpact = costDiff > 0
        ? `+$${costDiff.toFixed(2)}/hr more expensive`
        : `-$${Math.abs(costDiff).toFixed(2)}/hr cheaper`;

      toast({
        title: `Switching to ${target.name}`,
        description: `${costImpact} • ${target.description}`,
      });

      await onModelChange(modelId);
      setIsOpen(false);
    } catch (error: any) {
      toast({
        title: "Model Switch Failed",
        description: error.message || "Failed to switch AI model",
        variant: "destructive",
      });
    } finally {
      setSwitching(false);
    }
  };

  const currentModelConfig = MODELS[currentModel] || ALL_MODELS[currentModel];
  const currentModelLabel = currentModel === 'MISTRAL'
    ? 'Qwen3 14B'
    : currentModel;

  return (
    <div className="relative">
      {/* Dropdown Trigger */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled || switching}
        className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors border whitespace-nowrap ${
          disabled || switching
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
            : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300 hover:border-gray-400'
        }`}
        title={`Current: ${currentModelConfig.name} - $${currentModelConfig.costPerHour}/hr`}
      >
        <Zap className="h-4 w-4" />
        <span>Model: {currentModelLabel}</span>
        <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full right-0 mt-1 w-80 max-w-[90vw] bg-white border border-gray-200 rounded-lg shadow-lg z-[9999]">
          <div className="p-2">
            {Object.values(MODELS).map((model) => (
              <button
                key={model.id}
                onClick={() => handleModelSelect(model.id)}
                disabled={switching}
                className={`w-full p-3 rounded-lg text-left transition-colors ${
                  model.id === currentModel
                    ? 'bg-blue-50 border-2 border-blue-200'
                    : 'hover:bg-gray-50 border-2 border-transparent'
                } ${switching ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="font-semibold text-gray-900">
                      {model.name}
                      {model.id === currentModel && (
                        <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                          Active
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600">{model.description}</div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-lg font-bold text-green-600">
                      <DollarSign className="h-4 w-4" />
                      {model.costPerHour}/hr
                    </div>
                    <div className="text-xs text-gray-500">{model.gpu} GPU</div>
                  </div>
                </div>
                
                <div className="text-xs text-gray-500">
                  <strong>Best for:</strong> {model.bestFor.join(', ')}
                </div>

                {/* Cost comparison */}
                {model.id !== currentModel && (
                  <div className="mt-2 text-xs">
                    {model.costPerHour > currentModelConfig.costPerHour ? (
                      <span className="text-orange-600">
                        +${(model.costPerHour - currentModelConfig.costPerHour).toFixed(2)}/hr more expensive
                      </span>
                    ) : (
                      <span className="text-green-600">
                        -${(currentModelConfig.costPerHour - model.costPerHour).toFixed(2)}/hr cheaper (70% savings!)
                      </span>
                    )}
                  </div>
                )}
              </button>
            ))}
          </div>
          
          {/* Safety Warning */}
          {aiStatus === 'running' && (
            <div className="border-t border-gray-200 p-3 bg-yellow-50">
              <div className="flex items-center gap-2 text-yellow-800 text-xs">
                <span>⚠️</span>
                <span>Switching will stop the current model to prevent double billing</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[9998]"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};










